import LegalSection, { LegalSection as LegalSectionType } from '@/components/ui/LegalSection';
import { createFileRoute } from '@tanstack/react-router';
import { FileText } from 'lucide-react';
import { useEffect } from 'react';

export const Route = createFileRoute('/_public/ai-disclaimer')({
  component: AIDisclaimerPage,
})

export default function AIDisclaimerPage() {
  useEffect(() => {
    document.title = 'AI Disclaimer | Swim Homes';
  }, []);

  const sections: LegalSectionType[] = [
    {
      title: "",
      content: [
        "Welcome to \"the Captain,\" Swim Homes' AI-powered assistant designed to provide general information about our For Sale By Owner (FSBO) mere posting services. \"The Captain\" is an artificial intelligence system that generates automated responses based on patterns learned from training data. This AI assistant is provided as a convenience tool to help answer basic questions about our services and guide you through the home selling process, but has significant limitations that all users must understand and acknowledge."
      ]
    },
    {
      title: "1. Introduction and AI Nature Disclosure",
      content: [
        "Welcome to \"the Captain,\" Swim Homes' AI-powered assistant designed to provide general information about our For Sale By Owner (FSBO) mere posting services. \"The Captain\" is an artificial intelligence system that generates automated responses based on patterns learned from training data. This AI assistant is provided as a convenience tool to help answer basic questions about our services and guide you through the home selling process, but has significant limitations that all users must understand and acknowledge."
      ]
    },
    {
      title: "2. AI Limitations and Hallucination Risk",
      content: [
        "\"The Captain\" utilizes artificial intelligence technology that can occasionally produce incorrect, incomplete, or misleading information through a phenomenon known as \"hallucinations.\" These are instances where the AI generates plausible-sounding but factually incorrect responses. While we strive to minimize such occurrences, they remain an inherent risk of current AI technology.",
        "Users must understand that Swim Homes does not guarantee the accuracy, completeness, or reliability of information provided by \"the Captain.\" We explicitly disclaim any liability for decisions made based on potentially inaccurate information provided by the AI assistant."
      ]
    },
    {
      title: "3. Paramountcy of Written Policies",
      content: [
        "Swim Homes' official written policies—specifically our Terms of Service, Privacy Policy, and any other official documentation available on our website—take absolute precedence over any information provided by \"the Captain.\" In the event of any contradiction, inconsistency, or discrepancy between information provided by \"the Captain\" and our official written policies, the official documentation governs exclusively.",
        "Users must refer to these official documents for authoritative information about our services, obligations, and policies. No statement made by \"the Captain\" can modify, amend, or override any provisions in our official written policies."
      ]
    },
    {
      title: "4. Explicit Limitation of Services",
      content: [
        "\"The Captain\" is strictly limited to providing general information about Swim Homes' FSBO mere posting services and offering basic guidance through the home selling process. The AI assistant cannot and does not:"
      ],
      subsections: [
        {
          title: "",
          content: [
            "Provide legal advice regarding real estate transactions, property laws, or contract preparation",
            "Offer financial advice regarding mortgages, financing options, or investment strategies",
            "Conduct property valuations or appraisals",
            "Interpret legal documents or contracts",
            "Represent you in any legal or real estate capacity",
            "Replace the services of a licensed real estate professional, attorney, or financial advisor",
            "Create a realtor-client or broker-client relationship"
          ]
        }
      ]
    },
    {
      title: "5. Prohibited Uses of AI",
      content: [
        "Users are strictly prohibited from using \"the Captain\" for any of the following purposes:"
      ],
      subsections: [
        {
          title: "",
          content: [
            "Attempting to manipulate, deceive, or override the AI's built-in limitations",
            "Using the AI to generate, promote, or distribute harmful, offensive, or illegal content",
            "Engaging in automated or programmatic interactions with the AI",
            "Reverse engineering, decompiling, or otherwise attempting to extract the underlying model, code, or training data of the AI",
            "Using the AI to collect data about other users or for competitive research purposes",
            "Any other use that violates our Terms of Service, applicable laws, or the rights of others"
          ]
        }
      ]
    },
    {
      title: "6. Not a Substitute for Professional Advice",
      content: [
        "Information provided by \"the Captain\" is for general informational purposes only and should never be interpreted as professional advice. Real estate transactions involve significant legal and financial considerations that vary by jurisdiction and individual circumstances. Users should consult with qualified professionals, including real estate attorneys, licensed real estate professionals, and financial advisors, before making any decisions related to real estate transactions."
      ]
    },
    {
      title: "7. Independent Verification Required",
      content: [
        "Users are solely responsible for independently verifying all information provided by \"the Captain\" before taking any action. This includes:"
      ],
      subsections: [
        {
          title: "",
          content: [
            "Consulting Swim Homes' official written policies and documentation",
            "Verifying information through authoritative sources",
            "Seeking advice from qualified professionals when appropriate",
            "Exercising your own judgment regarding the applicability of information to your specific circumstances"
          ]
        }
      ]
    },
    {
      title: "8. Data Logging and Privacy Considerations",
      content: [
        "All interactions with \"the Captain\" are logged, stored, and may be reviewed by Swim Homes staff for quality assurance, training purposes, and to improve our services. These conversation logs are subject to our Privacy Policy, which you can access on our website. By using \"the Captain,\" you explicitly consent to the collection, storage, and processing of your conversation data as outlined in our Privacy Policy.",
        "Do not share sensitive personal or financial information with \"the Captain\" beyond what is necessary for your basic inquiry. While we implement security measures to protect your information, no digital communication system is completely secure."
      ]
    },
    {
      title: "9. No Realtor-Client or Professional Relationship",
      content: [
        "Interacting with \"the Captain\" does not establish any form of professional relationship, including but not limited to a realtor-client relationship, broker-client relationship, or attorney-client relationship. As stated in our Terms of Service, Swim Homes provides a platform that allows individuals to list, buy, and sell homes without real estate agents, but we do not represent you in any legal or real estate capacity."
      ]
    },
    {
      title: "10. System Availability and Service Interruptions",
      content: [
        "\"The Captain\" is provided on an \"as is, where is\" basis, and Swim Homes does not guarantee continuous, uninterrupted access to the AI assistant. The system may experience downtime, delays, or errors due to technical issues, maintenance, updates, or other factors beyond our control. Swim Homes is not liable for any inconvenience, loss, or damage resulting from service interruptions or unavailability."
      ]
    },
    {
      title: "11. External AI Limitations",
      content: [
        "\"The Captain\" may incorporate or rely on third-party AI systems, models, or services. Swim Homes does not own, control, or have full visibility into these external AI components and their underlying algorithms. Accordingly, Swim Homes disclaims all liability for the functioning, responses, or availability of these third-party AI components. The limitations, disclaimers, and liability provisions in this disclaimer apply with equal force to any issues arising from these external AI components."
      ]
    },
    {
      title: "12. Geographic and Jurisdictional Limitations",
      content: [
        "\"The Captain\" is primarily designed to provide information relevant to real estate transactions in Ontario, Canada. Information provided may not be applicable or accurate for other jurisdictions. Users outside of Ontario should consult local experts familiar with their specific regional laws and regulations."
      ]
    },
    {
      title: "13. Limitation of Liability",
      content: [
        "To the fullest extent permitted by law, Swim Homes disclaims all liability for any damages or losses—whether direct, indirect, incidental, or consequential—arising from your reliance on information provided by \"the Captain.\" This includes but is not limited to financial losses, missed opportunities, or legal complications resulting from decisions made based on AI assistant interactions.",
        "Swim Homes' maximum liability for any claims related to the use of \"the Captain\" shall not exceed the amount paid by you to Swim Homes for services in the 12 months preceding the claim."
      ]
    },
    {
      title: "14. User Feedback and Reporting",
      content: [
        "We encourage users to report any inaccuracies, errors, or issues encountered while using \"the Captain.\" Your feedback helps us improve the system and provide better service. Reports can be submitted through our website or by contacting support@swimhomes.ca."
      ]
    },
    {
      title: "15. Changes to This Disclaimer",
      content: [
        "Swim Homes reserves the right to modify this disclaimer at any time without prior notice. Changes will be effective immediately upon posting to our website. Your continued use of \"the Captain\" after any modification indicates your acceptance of these changes."
      ]
    },
    {
      title: "16. Force Majeure",
      content: [
        "Swim Homes shall not be liable for any failure to perform its obligations related to \"the Captain\" where such failure is as a result of Acts of God (including fire, flood, earthquake, storm, hurricane or other natural disaster), war, invasion, act of foreign enemies, hostilities (whether war is declared or not), civil war, rebellion, revolution, insurrection, military or usurped power or confiscation, terrorist activities, nationalization, government sanction, blockage, embargo, labor dispute, strike, lockout or interruption or failure of electricity or internet service, or any other event beyond the reasonable control of Swim Homes."
      ]
    },
    {
      title: "17. Severability",
      content: [
        "If any provision of this AI Disclaimer is found to be unenforceable or invalid under any applicable law, such unenforceability or invalidity shall not render this AI Disclaimer unenforceable or invalid as a whole, and such provisions shall be deleted without affecting the remaining provisions herein."
      ]
    },
    {
      title: "18. Entire Agreement",
      content: [
        "This AI Disclaimer, together with the Terms of Service and Privacy Policy, constitute the entire agreement between you and Swim Homes regarding your use of \"the Captain,\" and supersede all prior and contemporaneous written or oral agreements, communications, and other understandings relating to the subject matter of this AI Disclaimer."
      ]
    },
    {
      title: "19. Non-Waiver",
      content: [
        "The failure of Swim Homes to exercise or enforce any right or provision of this AI Disclaimer shall not constitute a waiver of such right or provision. No waiver by Swim Homes of any term or condition set forth in this AI Disclaimer shall be deemed a further or continuing waiver of such term or condition or a waiver of any other term or condition."
      ]
    },
    {
      title: "20. Acknowledgment of Understanding",
      content: [
        "By using \"the Captain,\" you acknowledge that you have read, understood, and agree to the terms of this disclaimer. You accept that \"the Captain\" is an AI tool with inherent limitations, that you will verify any information provided, and that you will not rely solely on the AI assistant for important real estate decisions."
      ]
    },
    {
      title: "21. Contact Information",
      content: [
        "If you have any questions or concerns about this AI Disclaimer or your interactions with \"the Captain,\" please contact us at support@swimhomes.ca."
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-linear-to-b from-gray-50 to-white">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <FileText className="h-10 w-10 text-primary" />
          <h1 className="text-3xl font-bold text-gray-900">AI Disclaimer</h1>
        </div>
        <p className="text-gray-500 mb-8">Last updated: March 03, 2025</p>

        <div className="space-y-8">
          {sections.map((section, index) => (
            <LegalSection key={index} section={section} />
          ))}
        </div>
      </div>
    </div>
  );
} 